import { ArrowUp, ChevronDown, Lightbulb, Loader2, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import BlockEditorMarkdown from '@/components/tiptap/vMarkdown/BlockEditor/block-editor';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import DictateControls from '@/container/dictate-button/dictate-controls';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Card } from '@heidi/design-system/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP,
} from '@heidi/design-system/typography';
import { EditorType } from '@heidi/editor/core';
import { useTranslations } from '@heidi/runtime/i18n';

import { OnboardingFiles } from '../shared/OnboardingFiles';
import { OnboardingUploadFileButton } from '../shared/OnboardingUploadFileButton';

const TEMPLATE_REFINE_CHIP_KEYS = [
  'signup.refineChipBullets',
  'signup.refineChipShorter',
  'signup.refineChipRemoveSection',
  'signup.refineChipFormalTone',
] as const;

interface TemplateReviewStepProps {
  onSaveAndContinue: () => void;
  onApplyChanges?: (changeRequest: string) => void;
  onUndoLastTemplateEdit?: () => void;
  canUndoLastTemplateEdit?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  isLoading?: boolean;
  isApplying?: boolean;
  /**
   * True while the right-hand preview is generating the example note.
   * Used to block Continue so the user can't navigate away mid-generation
   * and come back to an infinite loading state.
   */
  isGeneratingExample?: boolean;
}

export function TemplateReviewStep({
  onSaveAndContinue,
  onApplyChanges,
  onUndoLastTemplateEdit,
  canUndoLastTemplateEdit = false,
  onEditingChange,
  isLoading = false,
  isApplying = false,
  isGeneratingExample = false,
}: TemplateReviewStepProps) {
  const { t } = useTranslations();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const handleEditOpenChange = (open: boolean) => {
    setIsEditOpen(open);
    onEditingChange?.(open);
  };
  const [isFocused, setIsFocused] = useState(false);

  const [changeRequest, setChangeRequest] = useState('');

  /**
   * Combined disabled state for the refine UI. The refine controls should
   * be inert whenever:
   *  - the step is loading (initial template render),
   *  - an apply/refine request is in flight, or
   *  - the right-hand example note is still generating (same rule as the
   *    Continue button, so the user can't kick off another mutation while
   *    the preview is still mid-generation).
   */
  const isDisabled = isLoading || isApplying || isGeneratingExample;

  const { editor } = useBlockEditorMarkdown({
    content: '',
    editable: !isDisabled,
    placeholder: t('signup.askForChangesPlaceholder'),
    autofocus: false,
    isTemplateEditor: true,
  });

  // Track change request text
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      const text = editor.getText().trim();
      setChangeRequest(text);
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  const hasChangeRequest = changeRequest.length > 0;

  useEffect(() => {
    if (!editor) return;

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    editor.on('focus', handleFocus);
    editor.on('blur', handleBlur);

    return () => {
      editor.off('focus', handleFocus);
      editor.off('blur', handleBlur);
    };
  }, [editor]);

  // Clear the change request text once the apply request finishes
  // (keeps the user's text visible while the template is generating)
  const wasApplyingRef = useRef(false);
  useEffect(() => {
    if (wasApplyingRef.current && !isApplying) {
      editor?.commands.clearContent();
      setChangeRequest('');
    }
    wasApplyingRef.current = isApplying;
  }, [isApplying, editor]);

  const handleCardClick = () => {
    if (editor && !isDisabled) {
      editor.commands.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isDisabled) {
      onSaveAndContinue();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <TypographyH2>{t('signup.reviewTemplate')}</TypographyH2>
        <TypographyP className="text-text-primary">
          {t('signup.reviewTemplateHint')}
        </TypographyP>
      </div>

      {/* Make changes option - collapsible */}
      <Collapsible open={isEditOpen} onOpenChange={handleEditOpenChange}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit justify-start gap-2 rounded-lg px-3 py-2 hover:bg-background-secondary"
          >
            <TypographyCaption>{t('signup.makeChangesNow')}</TypographyCaption>
            <ChevronDown
              size={16}
              className={cn(
                'text-text-tertiary transition-transform duration-200',
                isEditOpen && 'rotate-180'
              )}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <div className="flex flex-col gap-2">
            <Card
              className={cn(
                'flex h-[148px] w-[400px] flex-col justify-between border border-border transition',
                isFocused && 'border-border-pressed',
                isDisabled && 'border-border-disabled'
              )}
              onClick={handleCardClick}
            >
              <div
                className={cn(
                  'flex-1 overflow-y-auto px-4 pt-2.5 transition-colors',
                  isDisabled && '[&_.ProseMirror]:text-text-tertiary'
                )}
              >
                {editor && (
                  <BlockEditorMarkdown
                    testTag="review-template-editor"
                    editor={editor}
                    editorType={EditorType.TEMPLATE}
                    enableFindReplace={false}
                    enableSnippet={false}
                    enableBubbleMenu={true}
                    tabType={undefined}
                    className="text-sm"
                  />
                )}
              </div>
              <div className="flex flex-col gap-2 p-4">
                <OnboardingFiles />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <OnboardingUploadFileButton isPending={isDisabled} />
                  </div>
                  <div className="flex items-center gap-1">
                    <DictateControls
                      editor={editor}
                      disabled={isDisabled}
                      source="createTemplate"
                      size="md"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="md"
                          iconOnly
                          disabled={!hasChangeRequest || isDisabled}
                          onClick={() => {
                            if (onApplyChanges && changeRequest) {
                              onApplyChanges(changeRequest);
                            }
                          }}
                        >
                          {isApplying ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ArrowUp size={14} />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {t('signup.applyChanges')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </Card>

            <div className="relative w-[400px]">
              <div className="flex h-7 items-center gap-2 overflow-x-auto pr-12 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {canUndoLastTemplateEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        iconOnly
                        onClick={() => onUndoLastTemplateEdit?.()}
                        disabled={isDisabled}
                      >
                        <Undo2 size={12} className="rtl:-scale-x-100" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t('signup.undoLastChange')}
                    </TooltipContent>
                  </Tooltip>
                )}
                {TEMPLATE_REFINE_CHIP_KEYS.map((chipKey) => (
                  <Button
                    key={chipKey}
                    type="button"
                    variant="outline"
                    size="xs"
                    className="shrink-0 gap-1.5"
                    disabled={isDisabled}
                    onClick={() => onApplyChanges?.(t(chipKey))}
                  >
                    <Lightbulb size={12} />
                    {t(chipKey)}
                  </Button>
                ))}
              </div>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-surface-accent to-transparent" />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Continue button - pushed to bottom */}
      <div className="flex flex-1 items-end pt-4">
        <Button size="lg" type="submit" disabled={isDisabled} className="w-full">
          {t('signup.continue')}
        </Button>
      </div>
    </form>
  );
}
